import { useState, useEffect, useRef } from 'react';
import { apiFetch, formatDate, DPP_BASE_URL } from '../dppApi';

// The brand dashboard "Bequeathals" tab — a read-only transfer ledger for the
// brand's own pieces. A KPI summary sits at the top; below it, four collapsible
// groups mirror the Inquiries section's chevron pattern. This view reads only
// executed ownership events (GET /api/brand/bequeathals); pending inheritance
// designations are private to owners and never surface here.
const GROUPS = [
    { key: 'inheritance', title: 'Bequeathals', sum: 'Bequeathed inheritances' },
    { key: 'gift', title: 'Gifts', sum: 'Gifts between owners' },
    { key: 'initial_sale', title: 'Initial sale', sum: 'Original point of sale' },
    { key: 'resale', title: 'Resale', sum: 'Secondary market sales' },
];

// "Draft a message" fills the composer with a warm opening line tuned to the
// transfer type. Client-side only — a starting point the brand edits before
// sending (no AI round-trip, unlike the inquiries composer).
const DRAFT_TEMPLATE = {
    inheritance: (to, piece) => `Dear ${to}, we were moved to see the ${piece} pass into your care. Our atelier is here whenever you need us.`,
    gift: (to, piece) => `Dear ${to}, congratulations on your ${piece} — we would be delighted to welcome you to the maison.`,
    initial_sale: (to, piece) => `Dear ${to}, welcome to the maison. We would love to help you care for your ${piece}.`,
    resale: (to, piece) => `Dear ${to}, welcome — your ${piece} has found a new home with you. We are here for its care and provenance.`,
};

const BrandBequeathals = ({ token }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    // Bequeathals (inheritance) open by default; the rest start collapsed.
    const [collapsed, setCollapsed] = useState({ gift: true, initial_sale: true, resale: true });
    const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

    // Which row's Message composer is open (one at a time), and each row's draft.
    const [openMsg, setOpenMsg] = useState(null);
    const [drafts, setDrafts] = useState({});
    const toggleMsg = (key) => setOpenMsg(k => (k === key ? null : key));
    const setDraft = (key, text) => setDrafts(d => ({ ...d, [key]: text }));
    const autoGrow = (el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } };
    // Only one composer is open at a time; this ref points at its textarea. The
    // effect below re-fits the box to its content whenever the open row or its
    // draft changes — so "Draft a message" (which drops in a multi-line template)
    // and reopening a row with an existing draft both wrap and grow instead of
    // clipping the text to the single starting row.
    const composeRef = useRef(null);
    useEffect(() => {
        const el = composeRef.current;
        if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; }
    }, [openMsg, drafts]);

    // Shared email subject: the piece title plus its Passport ID, matching the
    // passport "Contact Merchant" subjects so the thread is identifiable.
    const mailSubject = (r) => `${r.product_name || 'your piece'}${r.sku_or_qr ? ` (Passport ID: ${r.sku_or_qr})` : ''}`;

    // Brands are authorised to see the new owner's identity. "Send" mails the
    // composed note; "Draft a message" pre-fills a warm, type-aware opener the
    // brand can edit first. No-op when the transfer has no owner email on file.
    const fillDraft = (key, groupKey, r) =>
        setDraft(key, (DRAFT_TEMPLATE[groupKey] || DRAFT_TEMPLATE.initial_sale)(r.to_name || 'there', r.product_name || 'piece'));
    const sendMessage = (key, r) => {
        const body = (drafts[key] || '').trim();
        if (!r.to_email || !body) return;
        window.location.href = `mailto:${r.to_email}?subject=${encodeURIComponent(mailSubject(r))}&body=${encodeURIComponent(body)}`;
    };
    // No piece title / Passport ID here — a gift isn't about the piece they own,
    // and tying it to that ID could read as "you're being gifted this item".
    const sendGift = (r) => {
        if (r.to_email) window.location.href = `mailto:${r.to_email}?subject=${encodeURIComponent('A gift for our esteemed customer')}`;
    };
    const passportHref = (r) => `${DPP_BASE_URL}/jewellery/${r.sku_or_qr}?as=brand`;

    useEffect(() => {
        apiFetch('/api/brand/bequeathals', { token })
            .then(setData)
            .catch(err => setError(err.message));
    }, [token]);

    if (error) return <p className="dpp-error">{error}</p>;
    if (!data) return <p className="collection-status collection-status--flush">Loading…</p>;

    const { summary, groups } = data;

    return (
        <div className="bequeathal-view">
            <div className="bequeathal-kpi-row">
                <div className="bequeathal-kpi">
                    <div className="bequeathal-kpi-n">{summary.in_circulation}</div>
                    <div className="bequeathal-kpi-l">Pieces in circulation</div>
                </div>
                <div className="bequeathal-kpi">
                    <div className="bequeathal-kpi-n">{summary.total_transfers}</div>
                    <div className="bequeathal-kpi-l">Total transfers</div>
                </div>
                <div className="bequeathal-kpi">
                    <div className="bequeathal-kpi-n">{summary.bequeathals}</div>
                    <div className="bequeathal-kpi-l">Bequeathals</div>
                </div>
            </div>

            {GROUPS.map(g => {
                const rows = groups[g.key] || [];
                const isCollapsed = !!collapsed[g.key];
                return (
                    <section className="inquiry-group" key={g.key}>
                        <button type="button" className="inquiry-group-title inquiry-group-toggle"
                            onClick={() => toggle(g.key)} aria-expanded={!isCollapsed}>
                            <span className={`inquiry-group-chevron${isCollapsed ? '' : ' is-open'}`} aria-hidden="true">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                            </span>
                            <span>{g.title}</span>
                            <span className="inquiry-group-count">{rows.length}</span>
                            <span className="bequeathal-group-sum">{g.sum}</span>
                        </button>
                        {!isCollapsed && (
                            rows.length === 0
                                ? <p className="collection-status collection-status--flush section-empty">Nothing here yet.</p>
                                : (
                                    <div className="bequeathal-ledger">
                                        {rows.map((r, i) => {
                                            const key = `${g.key}-${i}`;
                                            const msgOpen = openMsg === key;
                                            return (
                                            <div className="tx-row" key={key}>
                                                <div className="tx-top">
                                                    <div className="tx-thumb">
                                                        {r.image_url && <img src={r.image_url} alt={r.product_name || ''} />}
                                                    </div>
                                                    <div className="tx-body">
                                                        <div className="tx-name">{r.product_name || '—'}</div>
                                                        <div className="tx-sub">
                                                            {r.from_name || '—'}
                                                            <span className="tx-arrow">→</span>
                                                            <span className="tx-to">{r.to_name || '—'}</span>
                                                            <span className="tx-dot">·</span>
                                                            {formatDate(r.date)}
                                                            {r.sku_or_qr && <>
                                                                <span className="tx-dot">·</span>
                                                                <a className="passport-link" href={passportHref(r)} target="_blank" rel="noreferrer">Passport</a>
                                                            </>}
                                                        </div>
                                                    </div>
                                                    <div className="tx-acts">
                                                        {/* Envelope = message, gift box = send a gift. Icons side by
                                                            side; hovering (or focusing) one slides its plain-text label
                                                            in beside it — no bubble/pill. */}
                                                        <button type="button" className={`tx-ico${msgOpen ? ' is-active' : ''}`} onClick={() => toggleMsg(key)} aria-label="Message">
                                                            <span className="tx-ico-label">Message</span>
                                                            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="1.5" /><path d="M3.5 6.5 12 12.5l8.5-6" /></svg>
                                                        </button>
                                                        <button type="button" className="tx-ico" onClick={() => sendGift(r)} aria-label="Send a gift">
                                                            <span className="tx-ico-label">Send a Gift</span>
                                                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12v8H4v-8" /><rect x="2" y="8" width="20" height="4" /><path d="M12 8v12" /><path d="M12 8C12 8 11 4 8.5 4S6 7 8 8" /><path d="M12 8C12 8 13 4 15.5 4S18 7 16 8" /></svg>
                                                        </button>
                                                    </div>
                                                </div>
                                                {msgOpen && (
                                                    <div className="tx-compose">
                                                        <div className="inquiry-compose">
                                                            <textarea className="inquiry-compose-text" rows={1}
                                                                ref={composeRef}
                                                                value={drafts[key] || ''}
                                                                onChange={e => { setDraft(key, e.target.value); autoGrow(e.target); }} />
                                                            <div className="inquiry-compose-bar">
                                                                <button type="button" className="inquiry-draft" onClick={() => fillDraft(key, g.key, r)}>
                                                                    <svg className="inquiry-draft-ico" viewBox="0 0 24 24" aria-hidden="true">
                                                                        <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
                                                                        <path d="M18 15l.7 1.8L20.5 17.5 18.7 18.2 18 20l-.7-1.8L15.5 17.5 17.3 16.8z" />
                                                                    </svg>
                                                                    Draft a message
                                                                </button>
                                                                <button type="button" className="inquiry-send" disabled={!(drafts[key] || '').trim()}
                                                                    onClick={() => sendMessage(key, r)} aria-label="Send">
                                                                    <span className="inquiry-send-arrow" aria-hidden="true">→</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                )
                        )}
                    </section>
                );
            })}
        </div>
    );
};

export default BrandBequeathals;
